import { BackButton } from 'app/components/Releases/CreateReleaseOverlay/components/BackButton';
import { ContentContainer } from 'app/components/Core';
import { ContentContainerType } from 'types/enums';
import { OverlayHeader } from 'app/components/Dialogs';
import { AudioSelectList, SelectedAudioObject } from 'app/components/Audio';
import { addAlertAction } from 'app/store/Alerts/slice';
import { AlertIcon } from 'app/store/Alerts/slice.types';
import { useAppDispatch, useAppSelector } from 'app/store';
import { Release, ReleaseType } from 'types/releases';
import { useFetchValidationSchema } from 'app/utils/hooks';
import {
  getCreateInitialReleaseVersionLink,
  getDisabledAudios,
} from 'app/store/Releases/selectors';
import {
  soundCloudReleaseCreatedAction,
  tuneCoreReleaseCreatedAction,
} from 'app/store/Mixpanel/slice';
import { useAddVersions, useCreateRelease } from './hooks';
import { getReleaseTypeName } from '../utils';

interface ReleaseAudioSelectOverlayProps {
  onBack?: () => void;
  onClose: () => void;
  release?: Release;
}

export const ReleaseAudioSelectOverlay = ({
  onBack,
  onClose,
  release,
}: ReleaseAudioSelectOverlayProps) => {
  const dispatch = useAppDispatch();

  const createReleaseVersionLink = useAppSelector(
    getCreateInitialReleaseVersionLink
  );

  const disabledAudios = useAppSelector(getDisabledAudios);

  const {
    validationSchema,
    validationSchemaFetched,
  } = useFetchValidationSchema({ link: createReleaseVersionLink });

  const maxAudioCount = validationSchema?.properties?.versions?.maxItems || 25;

  const sendSnackbarMessage = (id: string) => {
    dispatch(
      addAlertAction({
        id,
        message: `${getReleaseTypeName(release?.type)} release is processing`,
        statusIcon: AlertIcon.LOADING,
        autoHideDuration: 15000,
      })
    );
  };

  const { handleCreateRelease } = useCreateRelease({
    sendSnackbarMessage,
    releaseType: release?.type,
  });

  const { handleAttachMoreVersions } = useAddVersions({
    release,
    sendSnackbarMessage,
    onCancel: onClose,
  });

  const handleOnAdd = async (data: SelectedAudioObject) => {
    if (release?.guid) {
      await handleAttachMoreVersions(data);
    } else {
      const createdRelease = await handleCreateRelease(data);
      if (createdRelease.type === ReleaseType.SOUNDCLOUD) {
        dispatch(
          soundCloudReleaseCreatedAction({
            releaseId: createdRelease.guid,
          })
        );
      }
      if (createdRelease.type === ReleaseType.TUNECORE) {
        dispatch(
          tuneCoreReleaseCreatedAction({
            releaseId: createdRelease.guid,
          })
        );
      }
    }
  };

  return (
    <>
      {onBack && <BackButton onClick={onBack} />}
      <ContentContainer
        type={ContentContainerType.LIST}
        sx={{
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <OverlayHeader headerText="Choose Audio files" />
        <AudioSelectList
          onCancel={onClose}
          onAdd={handleOnAdd}
          emptyStateMessage="It looks like you don't have any audio files to release. Upload audio to a song and try again."
          audioTypesFilterKeys={['Master', 'Mix', 'Bounce']}
          maxAudioCount={maxAudioCount}
          loading={!validationSchemaFetched}
          disabledAudios={disabledAudios}
          validationContext={
            release?.type === ReleaseType.TUNECORE ? release.type : undefined
          }
        />
      </ContentContainer>
    </>
  );
};
